import { z } from 'zod';
/**
 * Provisioning / lifecycle status of a business.
 * Doubles as the user-visible provisioning state (doc 04 §4.1) and the
 * idempotency guard for webhook handlers (ADR-0006).
 *
 *   draft ── owner fills company details ──> collecting_docs
 *   collecting_docs ── docs uploaded, bundle submitted ──> pending_review
 *   pending_review ── Twilio approved (webhook) ──> approved
 *   pending_review ── rejected + reasons ──> rejected ── owner fixes ──> pending_review
 *   approved ── claim ──> provisioning ── number purchased ──> active
 *   active ── suspended (abuse / non-payment) ──> suspended
 *   any ── owner archives (numbers released first) ──> archived
 */
export const ProvisioningStatus = z.enum([
    'draft',
    'collecting_docs',
    'pending_review',
    'rejected',
    'approved',
    'provisioning',
    'active',
    'suspended',
    'archived',
]);
/** Countries we launch in — determines the number-regulation regime (doc 04). */
export const Country = z.enum(['FR', 'DE', 'PL']);
/**
 * Currency a business prices its services in — ISO 4217, derived from its
 * country (see `COUNTRY_DEFAULTS`), never asked for separately.
 *
 * Stored on the business rather than on each service: a salon does not price
 * one haircut in złoty and the next in euro, and a service that carried its own
 * currency could drift out of step with the business after a region change.
 */
export const Currency = z.enum(['PLN', 'EUR']);
/** Assistant language, per business. */
export const Language = z.enum(['fr', 'de', 'en', 'pl']);
/**
 * The voice the assistant speaks in, per business — an OpenAI Realtime voice id.
 *
 * A curated subset, not everything the API accepts: these are the voices that
 * hold up over a phone line (8 kHz μ-law) and whose perceived gender is
 * unambiguous enough to label in the settings UI. Callers hear this on every
 * word of a real conversation, so it is the owner's choice, not a deployment's.
 */
export const AssistantVoice = z.enum(['marin', 'coral', 'sage', 'cedar', 'ash', 'verse']);
/**
 * Perceived gender of each voice.
 *
 * Needed because not everything a caller hears comes from OpenAI: the messages
 * we cannot afford an AI session for — "we can't take bookings right now", the
 * daily-cap notice — are spoken by the carrier's own text-to-speech (doc 19
 * D1). Those pick a voice from this, so a business whose assistant is a woman
 * does not answer its overflow calls as a man. It can never be the *same*
 * voice — different engine entirely — but it must not be the opposite one.
 */
export const ASSISTANT_VOICE_GENDER = {
    marin: 'female',
    coral: 'female',
    sage: 'female',
    cedar: 'male',
    ash: 'male',
    verse: 'male',
};
/** Telephony/number provider we buy a country's numbers from (see `COUNTRY_DEFAULTS`). */
export const NumberProvider = z.enum(['twilio', 'telnyx']);
/** Number type we request per country (vocabulary shared across providers). */
export const NumberType = z.enum(['national', 'local', 'mobile', 'toll-free']);
/**
 * Per-(country,numberType) regulatory bundle state (doc 05 §3). One bundle backs
 * any number of numbers of the same country+type. Mapped from Twilio's bundle
 * statuses: `pending-review`/`in-review` -> `pending_review`,
 * `provisionally-approved` -> `provisionally_approved`, `twilio-approved` ->
 * `approved`, `twilio-rejected` -> `rejected`.
 */
export const BundleStatus = z.enum([
    'draft',
    'pending_review',
    'provisionally_approved',
    'approved',
    'rejected',
]);
/** Lifecycle of an individual purchased number (doc 05 §3, `numbers` subcollection). */
export const NumberLifecycle = z.enum(['purchasing', 'active', 'released', 'failed']);
/**
 * Outcome of a completed call, shown on the dashboard. `cancelled` and
 * `rescheduled` record calls that CHANGED an existing appointment rather than
 * creating one (doc 14 D5); precedence when several things happen in one call
 * is booked > rescheduled > cancelled > no_booking.
 */
export const CallOutcome = z.enum([
    'booked',
    'no_booking',
    'error',
    'abandoned',
    'cancelled',
    'rescheduled',
]);
/** Appointment lifecycle. Owners may only move confirmed -> cancelled (rules). */
export const AppointmentStatus = z.enum(['confirmed', 'cancelled']);
/** How an appointment was created. */
export const AppointmentSource = z.enum(['ai_call', 'manual']);
/** Transcript timeline author. `tool` = an assistant tool call + its result,
 * interleaved into the same chronological stream as the spoken turns. */
export const TranscriptRole = z.enum(['assistant', 'customer', 'tool']);
/**
 * Why the assistant could not help (doc 23 P0).
 *
 * A deliberately COARSE vocabulary — the point is to count how often each
 * shape of dead end happens, not to classify perfectly. Each value maps to a
 * decision we would take if it turned out to dominate:
 *
 * - `message_for_staff` → build the take_message tool (doc 23 §5b)
 * - `business_info`     → the facts block covers it (doc 23 P1)
 * - `price_or_quote`    → services carry prices; a gap here means the menu is
 *                          thin, or callers want quotes we cannot give
 * - `service_not_offered` → the caller wanted something off the menu
 * - `no_availability`   → they wanted a day or time we had nothing free for
 *                          (doc 30 §7). The clearest capacity signal there is:
 *                          "eleven people wanted Tuesday and we were full" is
 *                          an argument for opening another chair, and until
 *                          this existed every one of those calls was discarded.
 * - `urgent`            → the triage gap (doc 23 §5c). Watch this one closely:
 *                          it is the only value that is also a safety signal.
 * - `other`             → expected to dominate at first, which is the finding
 */
export const UnmetRequestKind = z.enum([
    'message_for_staff',
    'business_info',
    'price_or_quote',
    'service_not_offered',
    'no_availability',
    'urgent',
    'other',
]);
/**
 * What kind of thing the caller left behind (doc 28 D5).
 *
 * Four values, and no more, because the first thing an owner does with a
 * message is decide whether to ring back — that is all the structure has to
 * support. `comment` is the catch-all; the model is told to use it rather than
 * force a bad fit, for the same reason `UnmetRequestKind` has `other`.
 */
export const CallerMessageKind = z.enum(['problem', 'question', 'request', 'comment']);
/**
 * Billing state of one business (doc 23 — paid from day one).
 *
 * Mirrors the Stripe subscription statuses we actually act on, rather than all
 * of them: `incomplete_expired`, `paused` and friends collapse into `canceled`
 * because the product does the same thing in each case. `none` is the state
 * before checkout was ever completed — every business starts there.
 */
export const SubscriptionStatus = z.enum([
    'none',
    'trialing',
    'active',
    'past_due',
    'canceled',
]);
/** The three published plans. The Stripe price id for each is deployment
 *  configuration, not code — see `STRIPE_PRICE_*` params in `api/src/secrets.ts`. */
export const PlanTier = z.enum(['starter', 'pro', 'business']);
/**
 * What kind of business this is (doc 23).
 *
 * Collected once, at creation, and never edited: it is the segmentation key
 * doc 23 §7.3 asks for ("which verticals actually convert/retain"), and a field
 * owners can flip turns that history into noise. An owner whose business really
 * is something else archives it and creates a new one — which is also the
 * honest thing, since the services, hours and client base underneath belong to
 * the old shape.
 *
 * The order is doc 23's: tier 1 (served completely today) first, then tier 2
 * (works, but with a named gap), then `other`. NOTHING in the product branches
 * on this value today — no prompt insert, no gating — and that is deliberate
 * until the segmentation data says which vertical is worth the fork (doc 23
 * §7.2: one script with inserts, never N scripts).
 */
export const BusinessType = z.enum([
    // ── Tier 1: fixed-menu, fixed-duration, staff-based personal services ──
    'hair_barber',
    'beauty_nails',
    'massage_spa',
    'tattoo_piercing',
    'dog_grooming',
    'driving_school',
    'photography',
    // ── Tier 2: the same shape, each with one named gap (doc 23 §5) ──
    'physiotherapy',
    'dental',
    'veterinary',
    'auto_repair',
    // Deliberately last, and deliberately present: without it an owner we did
    // not anticipate either lies or abandons setup, and both are worse for the
    // segmentation this field exists to feed.
    'other',
]);
/** Every business type, in the order the picker shows them. */
export const BUSINESS_TYPES = BusinessType.options;
/**
 * Types doc 23 calls tier 2: they work, but each has a named gap (recurring
 * courses, urgency triage, take-a-message). Kept as data so a later decision —
 * a caveat in the UI, a different onboarding, a segment in a report — has one
 * list to read rather than a comment to re-derive.
 */
export const TIER_TWO_BUSINESS_TYPES = [
    'physiotherapy',
    'dental',
    'veterinary',
    'auto_repair',
];
//# sourceMappingURL=enums.js.map